import {
	Box,
	Checkbox,
	Heading,
	Radio,
	RadioGroup,
	Slider,
	SliderFilledTrack,
	SliderMark,
	SliderThumb,
	SliderTrack,
	Stack,
	Text
} from "@chakra-ui/react";
import React from "react";
import {AUDIO_MODE_DOWNLOAD, AUDIO_MODE_STREAM, AudioMode} from "../../types";

type SoundSettingsTabProps = {
	canEdit: boolean;
	appOverridesSystemVolume: boolean;
	disableVolumeButtons: boolean;
	setDefaultVolumeOnReset: boolean;
	pendingDefaultVolume: number;
	audioMode: AudioMode | undefined;
	isAudioModeAvailable: boolean;
	onAppOverridesSystemVolumeChange: (value: boolean) => void;
	onDisableVolumeButtonsChange: (value: boolean) => void;
	onSetDefaultVolumeOnResetChange: (value: boolean) => void;
	onPendingDefaultVolumeChange: (value: number) => void;
	onDefaultVolumeCommit: (value: number) => void;
	onAudioModeChange: (value: AudioMode) => void;
};

const resolveAudioMode = (value: string): AudioMode =>
	value === AUDIO_MODE_STREAM ? AUDIO_MODE_STREAM : AUDIO_MODE_DOWNLOAD;

const SoundSettingsTab = ({
	canEdit,
	appOverridesSystemVolume,
	disableVolumeButtons,
	setDefaultVolumeOnReset,
	pendingDefaultVolume,
	audioMode,
	isAudioModeAvailable,
	onAppOverridesSystemVolumeChange,
	onDisableVolumeButtonsChange,
	onSetDefaultVolumeOnResetChange,
	onPendingDefaultVolumeChange,
	onDefaultVolumeCommit,
	onAudioModeChange
}: SoundSettingsTabProps) => {
	return (
		<Stack spacing={6}>
			<Box>
				<Heading size={"sm"} mb={2}>Volume</Heading>
				<Stack spacing={3}>
					<Checkbox
						isChecked={appOverridesSystemVolume}
						onChange={(event) => onAppOverridesSystemVolumeChange(event.target.checked)}
						isDisabled={!canEdit}
					>
						App overrides system volume
					</Checkbox>
					<Checkbox
						isChecked={disableVolumeButtons}
						onChange={(event) => onDisableVolumeButtonsChange(event.target.checked)}
						isDisabled={!canEdit}
					>
						Disable volume buttons for the app
					</Checkbox>
					<Checkbox
						isChecked={setDefaultVolumeOnReset}
						onChange={(event) => onSetDefaultVolumeOnResetChange(event.target.checked)}
						isDisabled={!canEdit}
					>
						Set default volume on reset
					</Checkbox>
					<Box width={"50%"}>
						<Text fontWeight={"semibold"} mb={2}>Default volume: {pendingDefaultVolume}%</Text>
						<Box paddingInlineEnd={8} paddingBottom={4}>
							<Slider
								min={40}
								max={100}
								step={1}
								value={pendingDefaultVolume}
								onChange={onPendingDefaultVolumeChange}
								onChangeEnd={onDefaultVolumeCommit}
								isDisabled={!canEdit}
							>
								{Array.from({length: 7}, (_, idx) => 40 + idx * 10).map((val) => (
									<SliderMark
										key={`volume-tick-${val}`}
										value={val}
										boxSize={1}
										borderRadius={"full"}
										bg={"gray.300"}
										mt={3}
									/>
								))}
								<SliderMark value={40} mt={3} fontSize={"xs"} color={"gray.600"}>40</SliderMark>
								<SliderMark value={100} mt={3} fontSize={"xs"} color={"gray.600"}>100</SliderMark>
								<SliderTrack bg={"gray.200"}>
									<SliderFilledTrack bg={"blue.500"}/>
								</SliderTrack>
								<SliderThumb/>
							</Slider>
						</Box>
					</Box>
				</Stack>
			</Box>
			{isAudioModeAvailable ? (
				<Box>
					<Heading size={"sm"} mb={2}>Audio mode</Heading>
					<RadioGroup
						value={audioMode || AUDIO_MODE_DOWNLOAD}
						onChange={(value) => onAudioModeChange(resolveAudioMode(value))}
						isDisabled={!canEdit}
					>
						<Stack spacing={2}>
							<Radio value={AUDIO_MODE_DOWNLOAD}>
								<Box>
									<Text fontWeight={600}>Download</Text>
									<Text fontSize={"sm"} color={"gray.600"}>
										The app downloads all sound files up-front and plays them offline.
									</Text>
								</Box>
							</Radio>
							<Radio value={AUDIO_MODE_STREAM}>
								<Box>
									<Text fontWeight={600}>Stream</Text>
									<Text fontSize={"sm"} color={"gray.600"}>
										The app streams sound files on demand. Requires app version 5 or later.
									</Text>
								</Box>
							</Radio>
						</Stack>
					</RadioGroup>
				</Box>
			) : null}
		</Stack>
	);
};

export default SoundSettingsTab;
